import * as React from 'react';
import { FacilityType, fieldValidation, rng } from '../../../amorg-common';
import { InspectionPointUsage } from '../../../amorg-common/DTOs/InspectionPoint';
import { createInspectionPoint, updateInspectionPoint } from '../../../api/safetyInspectionClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { Modal } from '../../../designSystem/Modal';
import { Toggletip } from '../../../designSystem/Toggletip';
import { Button } from '../../../designSystem/button/Button';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRowWithChildren } from '../../../designSystem/compositeComponents/ListRow';
import { FormField } from '../../../designSystem/formField/FormField';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { TextField } from '../../../designSystem/formField/text/TextField';
import { TextSaveField } from '../../../designSystem/formField/text/TextSaveField';
import { TextAreaField } from '../../../designSystem/formField/textArea/TextAreaField';
import { TextAreaSaveField } from '../../../designSystem/formField/textArea/TextAreaSaveField';
import { OnOff } from '../../../designSystem/inputs/OnOff';
import { P } from '../../../designSystem/typography/Body';
import { H2 } from '../../../designSystem/typography/Heading';
import { InfoChip } from '../../../designSystem/Chip';


export type InspectionPointConfigurationProps = {
    allFacilityTypes:FacilityType[],
    inspectionPointUsage:InspectionPointUsage[],
    onUpdated:()=>void
}
export function InspectionPointConfiguration(props:InspectionPointConfigurationProps):JSX.Element
{
    const [facilityTypeFilter, setFacilityTypeFilter] = React.useState('');
    const [newInspectionPoint, setNewInspectionPoint] = React.useState<InspectionPointUsage>(null);
    const [loading, setLoading] = React.useState(false);

    async function handleSaveInspectionPoint()
    {
        setLoading(true);
        await createInspectionPoint(
            rng.getRandomId(),
            newInspectionPoint.facilityTypeIds,
            newInspectionPoint.title,
            newInspectionPoint.instructions
        );
        setNewInspectionPoint(null);
        props.onUpdated();
        setLoading(false);
    }

    const displayedMappings = facilityTypeFilter === '' 
        ? props.inspectionPointUsage
        : props.inspectionPointUsage.filter(m => m.facilityTypeIds.some(f => f===facilityTypeFilter ));

    const activeMappings = displayedMappings.filter(m => m.facilityTypeIds.length > 0);
    const inactiveMappings = displayedMappings.filter(m => m.facilityTypeIds.length === 0);

    const facilityTypeOptions = props.allFacilityTypes.map(f => ({value:f.id, text:f.name}))
        .sort((a,b) => a.text.localeCompare(b.text));

    return (
        <div>
            <LoadingPageOverlay loading={loading} />
            <Flex flexDirection='column' gap={24}>

                <Flex justifyContent='space-between' alignItems='center' gap={32}>
                    <div>
                        <SelectField label='Filtrér efter facilitetstype' 
                            selectedValue={facilityTypeFilter}
                            onChange={(value) => setFacilityTypeFilter(value)}
                            options={[
                                {value:'', text:'Alle'},
                                ...facilityTypeOptions
                            ]} />
                    </div>
                    <Button variant='filled' text='Opret kontrolpunkt' icon='actions_plus' 
                        onClick={() => setNewInspectionPoint({
                                type:'custom',
                                inspectionPointId:'', 
                                facilityTypeIds:[], 
                                title:'', 
                                instructions:'', 
                                inUse:false
                        })} />
                </Flex>

                {activeMappings.length===0 && facilityTypeFilter !== '' &&
                        <span>Der er ingen kontrolpunkter tilknyttet  facilitetstypen <i>{props.allFacilityTypes.find(f => f.id === facilityTypeFilter)?.name}</i>.</span>
                }
            
                <Flex flexDirection='column' gap={2}>
                    {activeMappings.map((mapping, index) => 
                        <InspectionPointPanel key={index} inspectionPoint={mapping} allFacilityTypes={props.allFacilityTypes} onUpdated={props.onUpdated} />
                    )}
                </Flex>

                <div>
                    {inactiveMappings.length > 0 &&
                        <>
                            <H2>Ikke-anvendte kontrolpunkter</H2>
                            {inactiveMappings.map((mapping, index) => 
                                <InspectionPointPanel key={index} inspectionPoint={mapping} allFacilityTypes={props.allFacilityTypes} onUpdated={props.onUpdated} />
                            )}
                        </>
                    }
                </div>

            </Flex>

            <Modal title='Opret kontrolpunkt'
                showModal={newInspectionPoint !== null}
                onCancel={() => setNewInspectionPoint(null)}
                content={
                    <InspectionPointEditor 
                        inspectionPoint={newInspectionPoint}
                        allFacilityTypes={props.allFacilityTypes}
                        onChange={(inspectionPoint) => setNewInspectionPoint(inspectionPoint)}
                        onCancel={() => setNewInspectionPoint(null)}
                        onSave={handleSaveInspectionPoint} 
                    />
                } 
            />

            

        </div>
    );
}

type InspectionPointPanelProps = {
    inspectionPoint:InspectionPointUsage,
    allFacilityTypes:FacilityType[],
    onUpdated:()=>void
}
function InspectionPointPanel(props:InspectionPointPanelProps):JSX.Element
{
    const [showFacilityTypeSelector, setShowFacilityTypeSelector] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    async function handleClickRemoveFacilityType(identifier:string)
    {
        setLoading(true);
        await updateInspectionPoint(
            props.inspectionPoint.inspectionPointId,
            props.inspectionPoint.facilityTypeIds.filter(f => f !== identifier),
            props.inspectionPoint.title,
            props.inspectionPoint.instructions
        );
        setLoading(false);
        
        props.onUpdated();
    }

    async function handleSaveTitle(value:string)
    {
        if(fieldValidation.inspectionPointModification.title(value)){
            setLoading(true);
            await updateInspectionPoint(
                props.inspectionPoint.inspectionPointId,
                props.inspectionPoint.facilityTypeIds,
                value,
                props.inspectionPoint.instructions
            );
            setLoading(false);
            props.onUpdated();
        }
    }

    async function handleSaveInstructions(value:string)
    {
        setLoading(true);
        await updateInspectionPoint(
            props.inspectionPoint.inspectionPointId,
            props.inspectionPoint.facilityTypeIds,
            props.inspectionPoint.title,
            value
        );
        setLoading(false);
        props.onUpdated();
    }

    function handleSavedFacilityTypes()
    {
        setShowFacilityTypeSelector(false);
        props.onUpdated();
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <ListRowWithChildren styleVariant='neutral' onCollapse={() => { return; }}
                childRows={
                    <ListRowWithChildren styleVariant='neutral'
                        content={
                            <>
                                <P style={{margin:'10px 20px 20px 20px'}}>
                                    <TextSaveField
                                        label='Titel'
                                        value={props.inspectionPoint.title} 
                                        onSave={handleSaveTitle} 
                                        disabled={props.inspectionPoint.inUse}
                                        validator={(value) => fieldValidation.inspectionPointModification.title(value)
                                            ? { valid: true } : { valid: false, errorMessage: 'Ugyldig titel - skal udfyldes med maks. 100 tegn'}}
                                    />
                                </P>
                                <P style={{margin:'10px 20px 20px 20px'}}>
                                    <TextAreaSaveField 
                                        tooltip={
                                            <P>
                                                Instruktionen er en beskrivelse af hvad der skal kontrolleres, og vises for brugeren under inspektionen.<br />
                                                Hvis teksten er lang, vil den automatisk blive foldet ved første tomme linie.
                                            </P>
                                        }
                                        label='Instruktion'
                                        value={props.inspectionPoint.instructions} 
                                        onSave={handleSaveInstructions} 
                                        disabled={props.inspectionPoint.inUse}
                                        validator={(value) => fieldValidation.inspectionPointModification.instructions(value)
                                            ? { valid:true } : { valid:false, errorMessage:'Ugyldig instruktion - skal udfyldes med maks. 1000 tegn' }}
                                    />
                                </P>
                                <Flex gap={4} style={{margin:'0px 20px', flexWrap:'wrap'}}>
                                    {props.inspectionPoint.facilityTypeIds.map((facilityTypeId) =>
                                        <InfoChip key={facilityTypeId}>
                                            {props.allFacilityTypes.find(f => f.id === facilityTypeId)?.name}
                                        </InfoChip>
                                    )}
                                    
                                </Flex>
                                <Flex justifyContent='flex-end'>
                                    <Button variant='text' text='Rediger facilitetstyper' icon='navigation_settings' onClick={() => setShowFacilityTypeSelector(true)} />
                                </Flex>
                            </>
                        }/>
                }
                content={
                    <Flex gap={4}>
                        <>
                            {props.inspectionPoint.title}
                            {props.inspectionPoint.inUse &&
                                <Toggletip icon='status_error'>
                                    <P>
                                        Kontrolpunktet er i brug for en eller flere sikkerhedsrunderinger.<br />
                                        Titel og instruktion kan der ikke redigeres.
                                    </P>
                                </Toggletip>
                            }
                        </>
                    </Flex>
                } />

            <Modal title='Rediger facilitetstyper'
                showModal={showFacilityTypeSelector}
                onCancel={() => setShowFacilityTypeSelector(false)}
                content={
                    <ConfigurationEditor inspectionPoint={props.inspectionPoint} allFacilityTypes={props.allFacilityTypes}
                        onCancel={() => setShowFacilityTypeSelector(false)}
                        onSaved={handleSavedFacilityTypes} />
                }  
            />
        </>
    );
}

type InspectionPointEditorProps = {
    inspectionPoint:InspectionPointUsage,
    allFacilityTypes:FacilityType[],
    onChange:(inspectionPoint:InspectionPointUsage)=>void,
    onSave:()=>void,
    onCancel:()=>void
}
function InspectionPointEditor(props:InspectionPointEditorProps):JSX.Element
{
    const [shouldValidate, setShouldValidate] = React.useState(false);

    const sortedFacilityTypes = React.useMemo(() => {
        return props.allFacilityTypes.map(f => f).sort((a,b) => a.name.localeCompare(b.name))
    }, []);

    function handleChangeTitle(value:string)
    {
        props.onChange(
            {
                ...props.inspectionPoint,
                title:value
            }
        )
    }

    function handleChangeInstructions(value:string)
    {
        props.onChange(
            {
                ...props.inspectionPoint,
                instructions:value
            }
        )
    }

    function handleChangeAllFacilityTypes(checked:boolean)
    {
        if(checked)
            props.onChange({
                ...props.inspectionPoint,
                facilityTypeIds: props.allFacilityTypes.map(ft => ft.id)
            });
        else
            props.onChange({
                ...props.inspectionPoint,
                facilityTypeIds: []
            });
    }

    function handleChangeFacilityTypes(value:string, checked:boolean)
    {
        if(checked)
            props.onChange({
                ...props.inspectionPoint,
                facilityTypeIds: [...props.inspectionPoint.facilityTypeIds, value]
            });
        else
            props.onChange({
                ...props.inspectionPoint,
                facilityTypeIds: props.inspectionPoint.facilityTypeIds.filter(f => f !== value)
            });
    }

    function handleClickSave()
    {
        const validTitle = fieldValidation.inspectionPointModification.title(props.inspectionPoint.title);
        const validInstructions = fieldValidation.inspectionPointModification.instructions(props.inspectionPoint.instructions);
        setShouldValidate(true);
        if(validTitle && validInstructions)
        {
            props.onSave();
        }
    }

    return (
        <Flex flexDirection='column' gap={16}>

            <TextField
                label='Titel'
                value={props.inspectionPoint.title}
                onChange={handleChangeTitle}
                validator={(value: string) =>
                    fieldValidation.inspectionPointModification.title(value) || !shouldValidate
                        ? { valid: true }
                        : { valid: false, errorMessage: `Ugyldig titel - skal udfyldes med maks. 100 tegn (${value.length})` }
                } />

            <TextAreaField
                label='Instruktion'
                value={props.inspectionPoint.instructions}
                minRows={2}
                onChange={handleChangeInstructions}
                validator={(value: string) =>
                    fieldValidation.inspectionPointModification.instructions(value) || !shouldValidate
                        ? { valid: true }
                        : { valid: false, errorMessage: `Ugyldig instruktion - skal udfyldes med maks. 1000 tegn (${value.length})` }
                } />

            <FormField
                label='Facilitetstyper'
                inputComponent={

                    <Flex flexDirection="column" gap={16}>
                        <Flex gap={4} alignItems='center'>
                            <OnOff 
                                active={props.allFacilityTypes.every(ft => props.inspectionPoint.facilityTypeIds.find(ft2 => ft2 === ft.id))} 
                                onClick={handleChangeAllFacilityTypes} />
                            Alle
                        </Flex>
                        
                        <div style={{ columns:'2 auto' }}>
                            {sortedFacilityTypes.map(f => 
                                <Flex key={f.id} gap={4} alignItems='center' style={{marginTop:'4px'}}>
                                    <OnOff 
                                        active={props.inspectionPoint.facilityTypeIds.some(ft => ft === f.id)} 
                                        onClick={(active) => handleChangeFacilityTypes(f.id, active)} />
                                    {f.name}
                                </Flex>
                            )}
                        </div>

                    </Flex>
                } />

            <Flex justifyContent='flex-end' gap={8}>
                <Button variant='outlined' text='Annuller' onClick={props.onCancel} />
                <Button variant='filled' text='Gem' onClick={handleClickSave} />
            </Flex>
        </Flex>
    );
}


type ConfigurationEditorProps = {
    inspectionPoint:InspectionPointUsage,
    allFacilityTypes:FacilityType[],
    onSaved:()=>void,
    onCancel:()=>void
}
function ConfigurationEditor(props:ConfigurationEditorProps):JSX.Element
{
    const [selectedFacilityTypeIds, setSelectedFacilityTypeIds] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(()=>{
        setSelectedFacilityTypeIds(props.inspectionPoint.facilityTypeIds);
    }, [props.inspectionPoint.facilityTypeIds]);

    const sortedFacilityTypes = React.useMemo(() => {
        return props.allFacilityTypes.map(f => f).sort((a,b) => a.name.localeCompare(b.name))
    }, []);

    function handleChange(value:string, checked:boolean)
    {
        if(checked)
            setSelectedFacilityTypeIds([...selectedFacilityTypeIds, value]);
        else
            setSelectedFacilityTypeIds(selectedFacilityTypeIds.filter(f => f !== value));
    }

    async function handleSave()
    {
        setLoading(true);
        await updateInspectionPoint(
            props.inspectionPoint.inspectionPointId, 
            selectedFacilityTypeIds,
            props.inspectionPoint.title,
            props.inspectionPoint.instructions
        );
        setLoading(false);
        props.onSaved();
    }

    return (
        <Flex flexDirection='column' gap={16}>
            <LoadingPageOverlay loading={loading} />

            <Flex gap={4} alignItems='center'>
                <OnOff 
                    active={props.allFacilityTypes.every(ft => selectedFacilityTypeIds.find(ft2 => ft2 === ft.id))} 
                    onClick={(active) => setSelectedFacilityTypeIds(
                            active ? props.allFacilityTypes.map(ft => ft.id) : []
                        )} />
                Alle
            </Flex>
            
            <div style={{ columns:'2 auto' }}>
                {sortedFacilityTypes.map(f => 
                    <Flex key={f.id} gap={4} alignItems='center' style={{marginTop:'4px'}}>
                        <OnOff 
                            active={selectedFacilityTypeIds.some(ft => ft === f.id)} 
                            onClick={(active) => handleChange(f.id, active)} />
                        {f.name}
                    </Flex>
                )}
            </div>
        

            <Flex justifyContent='flex-end' gap={4}>
                <Button variant='outlined' text='Annuller' onClick={props.onCancel} />
                <Button variant='filled' text='Gem' onClick={handleSave} />
            </Flex>

        </Flex>
    );
}